import { ComponentProps, RefObject, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CellContext } from '@tanstack/react-table'
import { ActionsDropdown } from '../ActionsDropdown/ActionsDropdown'
import { useBillingApi } from '../../../../../hooks/useApi/useBillingApi/useBillingApi'
import useToastStore from '../../../../../stores/useToastStore/useToastStore'
import { useNavigate } from '@tanstack/react-router'
import { Payable } from '../../../../../hooks'

type ActionCellProps = CellContext<Payable, unknown> & {
    containerRef?: RefObject<HTMLDivElement>
}

export const ActionsCell: React.FC<ActionCellProps> = ({
    row,
    containerRef,
}) => {
    const invoiceDocId = row.original.invoiceDocId

    const navigate = useNavigate()

    const onViewClick = useCallback(() => {
        void navigate({ to: `/invoices/${row.original.invoiceNo}` })
    }, [navigate, row.original.invoiceNo])

    if (invoiceDocId) {
        return (
            <ActionsDropdownWithDownload
                invoiceDocId={invoiceDocId}
                onViewClick={onViewClick}
                containerRef={containerRef}
            />
        )
    }

    return (
        <ActionsDropdown
            onViewClick={onViewClick}
            containerRef={containerRef}
        />
    )
}

type ActionsDropdownWithDownloadProps = Pick<
    ComponentProps<typeof ActionsDropdown>,
    'onViewClick' | 'containerRef'
> & {
    invoiceDocId: number
}
const ActionsDropdownWithDownload: React.FC<
    ActionsDropdownWithDownloadProps
> = (props) => {
    const { invoiceDocId, onViewClick, containerRef } = props
    const { useGetDocument } = useBillingApi()
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')

    const { refetch } = useGetDocument(invoiceDocId, {
        onError: () => {
            addAlert({
                variant: 'error',
                title: t('common.alerts.download_failed.title'),
                description: t('common.alerts.download_failed.description'),
            })
        },
    })

    const onDownloadClick = useCallback(() => {
        void refetch()
    }, [refetch])

    return (
        <ActionsDropdown
            onDownloadClick={onDownloadClick}
            onViewClick={onViewClick}
            containerRef={containerRef}
        />
    )
}
